import React, { useEffect, useState } from "react";
import { apiPost, apiDelete, apiGet } from "../../api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../hooks/usePermissions";
import Pagination from "../../components/Pagination";
import SortableTableHeader from "../../components/SortableTableHeader";
import { Plus, Pencil, Trash2, KeyRound, ToggleLeft } from "lucide-react";

function getDefaultDates() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

const ModeratorsList = () => {
  const [moderators, setModerators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [filters, setFilters] = useState({
    ...getDefaultDates(),
    status: "Active",
  });
  const [tempFilters, setTempFilters] = useState({
    ...getDefaultDates(),
    status: "Active",
  });
  const [sort, setSort] = useState({ key: "", direction: "asc" });
  const [sortedModerators, setSortedModerators] = useState([]);
  const [useDates, setUseDates] = useState(false);
  const [tempUseDates, setTempUseDates] = useState(false);
  const navigate = useNavigate();
  const { can } = usePermissions();

  const fetchModerators = async (pageNum = 1, filterVals = filters) => {
    setLoading(true);
    try {
      let query = `/api/admin/moderators/list?page=${pageNum}&pageSize=30&order=desc`;
      if (filterVals.status && filterVals.status !== "All") {
        query += `&status=${filterVals.status}`;
      }
      if (useDates) {
        query += `&startDate=${filterVals.startDate}&endDate=${filterVals.endDate}`;
      }
      const res = await apiGet(query, {});
      const newModerators = res.data.data.data;
      setModerators(newModerators);
      setSortedModerators(newModerators);
      setTotalPages(res.data.data.totalPages);
      setTotalRows(res.data.data.totalRows || 0);
    } catch (err) {
      toast.error("Failed to fetch moderators");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModerators(page, filters);
    // eslint-disable-next-line
  }, [page, filters, useDates]);

  // Client-side sorting effect
  useEffect(() => {
    if (!moderators.length) return;

    const sorted = [...moderators].sort((a, b) => {
      if (!sort.key) return 0;

      let aValue = a[sort.key];
      let bValue = b[sort.key];

      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";

      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();

      if (sort.direction === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    setSortedModerators(sorted);
  }, [moderators, sort]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this moderator?")) return;
    try {
      await apiDelete(`/api/admin/moderators/delete/${id}`);
      toast.success("Moderator deleted successfully");
      fetchModerators(page, filters);
    } catch (err) {
      toast.error("Failed to delete moderator");
    }
  };

  const handleStatus = async (id) => {
    try {
      await apiPost("/api/admin/moderators/update-status", { moderatorId: id });
      toast.success("Moderator status updated");
      fetchModerators(page, filters);
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleTempFilterChange = (e) => {
    setTempFilters({ ...tempFilters, [e.target.name]: e.target.value });
  };

  const handleTempDateToggle = (e) => {
    setTempUseDates(e.target.checked);
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setUseDates(tempUseDates);
    setPage(1);
  };

  const clearFilters = () => {
    const defaultFilters = { ...getDefaultDates(), status: "Active" };
    setFilters(defaultFilters);
    setTempFilters(defaultFilters);
    setUseDates(false);
    setTempUseDates(false);
    setPage(1);
  };

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  return (
    <div className="w-full">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
            <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
            Management
          </div>
          <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">All <span className="text-gradient-ginox">Moderators</span></h1>
        </div>
        {can("POST:/api/admin/moderators/add") && (
          <button
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors flex items-center gap-1.5"
            onClick={() => navigate('/moderators/add')}
          >
            <Plus size={13} />
            Add Moderator
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Status Filter */}
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Status
            </label>
            <select
              name="status"
              value={tempFilters.status}
              onChange={handleTempFilterChange}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              <option value="All">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          {/* Date Filter Toggle */}
          <div className="flex items-center gap-2 pt-5">
            <input
              type="checkbox"
              id="useDates"
              checked={tempUseDates}
              onChange={handleTempDateToggle}
              className="h-3.5 w-3.5 rounded border border-border/70 accent-ginox-green"
            />
            <label
              htmlFor="useDates"
              className="text-sm text-muted-foreground cursor-pointer select-none"
            >
              Filter by Date
            </label>
          </div>
        </div>

        {/* Date Filters */}
        {tempUseDates && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={tempFilters.startDate}
                onChange={handleTempFilterChange}
                className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                max={tempFilters.endDate}
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={tempFilters.endDate}
                onChange={handleTempFilterChange}
                className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                min={tempFilters.startDate}
                max={new Date().toISOString().slice(0, 10)}
              />
            </div>
          </div>
        )}

        {/* Filter and Clear Buttons */}
        <div className="mt-4 flex gap-3">
          <button
            onClick={applyFilters}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
          >
            Apply Filters
          </button>
          <button
            onClick={clearFilters}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <SortableTableHeader
                sortKey="name"
                currentSort={sort}
                onSort={handleSort}
                className="text-left"
              >
                Name
              </SortableTableHeader>
              <SortableTableHeader
                sortKey="email"
                currentSort={sort}
                onSort={handleSort}
                className="text-left"
              >
                Email
              </SortableTableHeader>
              <SortableTableHeader
                sortKey="role"
                currentSort={sort}
                onSort={handleSort}
                className="text-left"
              >
                Role
              </SortableTableHeader>
              <SortableTableHeader
                sortKey="status"
                currentSort={sort}
                onSort={handleSort}
                className="text-left"
              >
                Status
              </SortableTableHeader>
              <SortableTableHeader sortable={false} className="text-left">
                Actions
              </SortableTableHeader>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="py-12 text-center text-sm text-muted-foreground">Loading...</td>
              </tr>
            ) : moderators.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-12 text-center text-sm text-muted-foreground">No data found</td>
              </tr>
            ) : (
              sortedModerators.map((m) => (
                <tr key={m._id} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-medium">
                    {m.name}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                    {m.email}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                    {m.role || "—"}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                    {m.status === "Active" ? (
                      <span className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-400">Active</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[10px] font-medium text-red-400">Inactive</span>
                    )}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                    <div className="flex flex-wrap items-center gap-2">
                      {can("POST:/api/admin/moderators/edit") && (
                        <button
                          className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5"
                          onClick={() => navigate(`/moderators/edit/${m._id}`)}
                        >
                          <Pencil size={12} />
                          Edit
                        </button>
                      )}
                      {can("POST:/api/admin/moderators/update-password") && (
                        <button
                          className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors flex items-center gap-1.5"
                          onClick={() => navigate(`/moderators/password/${m._id}`)}
                        >
                          <KeyRound size={12} />
                          Password
                        </button>
                      )}
                      {can("POST:/api/admin/moderators/update-status") && (
                        <button
                          className="rounded-md border border-ginox-green/30 bg-ginox-green/10 px-3 py-1.5 text-xs text-ginox-green hover:bg-ginox-green/20 transition-colors flex items-center gap-1.5"
                          onClick={() => handleStatus(m._id)}
                        >
                          <ToggleLeft size={12} />
                          Toggle
                        </button>
                      )}
                      {can("DELETE:/api/admin/moderators/delete/:id") && (
                        <button
                          className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/20 transition-colors flex items-center gap-1.5"
                          onClick={() => handleDelete(m._id)}
                        >
                          <Trash2 size={12} />
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalRows={totalRows}
        pageSize={30}
        onPageChange={(newPage) => setPage(newPage)}
      />
    </div>
  );
};

export default ModeratorsList;
